import React, { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link } from 'react-router-dom';
import { requestForgotPassword } from '../services/api';
import AuthShell from '../components/auth/AuthShell';
import AuthFormLayout from '../components/auth/AuthFormLayout';
import AuthErrorBanner from '../components/auth/AuthErrorBanner';

const forgotPasswordSchema = z.object({
  usernameOrEmail: z
    .string()
    .min(1, 'Username or email is required')
    .max(255, 'Input is too long'),
});

type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>;

const ForgotPasswordPage: React.FC = () => {
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationBanner, setValidationBanner] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
  });

  const dismissBanner = useCallback(() => {
    setError(null);
    setValidationBanner(null);
  }, []);

  const onSubmit = async (data: ForgotPasswordFormData) => {
    setIsLoading(true);
    setError(null);
    setValidationBanner(null);
    try {
      await requestForgotPassword(data.usernameOrEmail.trim());
      setSubmitted(true);
    } catch (err: any) {
      setError(
        err.response?.data?.error || 'Something went wrong. Please try again.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const onInvalid = () => {
    setError(null);
    setValidationBanner('Please enter your username or email.');
  };

  const bannerMessage = error ?? validationBanner;

  return (
    <AuthShell showBack backTo="/login" backLabel="Back to login">
      {bannerMessage && (
        <AuthErrorBanner message={bannerMessage} onDismiss={dismissBanner} />
      )}
      <AuthFormLayout
        heading="Forgot Password"
        subtext="Enter details and we'll send you a reset link"
      >
        {submitted ? (
          <div className="space-y-6">
            <div className="auth-success" role="status">
              Check your email for the reset link.
            </div>
            <div className="text-center">
              <Link to="/login" className="auth-link text-sm">
                Back to login
              </Link>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit, onInvalid)} className="space-y-4">
            <div>
              <input
                id="usernameOrEmail"
                type="text"
                className="auth-input"
                placeholder="Username or Email"
                autoComplete="username email"
                {...register('usernameOrEmail')}
              />
              {errors.usernameOrEmail && (
                <p className="auth-error">{errors.usernameOrEmail.message}</p>
              )}
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="auth-btn-primary"
              aria-busy={isLoading}
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="auth-btn-spinner" aria-hidden="true" />
                  Sending…
                </span>
              ) : (
                'Send reset link'
              )}
            </button>

            <div className="pt-3 text-center">
              <Link to="/login" className="auth-link text-sm">
                Back to login
              </Link>
            </div>
          </form>
        )}
      </AuthFormLayout>
    </AuthShell>
  );
};

export default ForgotPasswordPage;
